import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  mapOfferCampaignError,
  useAuthSession,
  useCreateOfferCampaign,
  useTranslation,
  type OfferCampaignErrorKey,
  type TranslationKey,
} from '@dukanti/shared';
import {
  OfferCampaignFormPage,
  type OfferCampaignFormSubmit,
} from '@/features/offer-campaigns/components/offer-campaign-form-page';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';

interface StoreContext {
  readonly storeId: string;
}

function toStoreContext(storeId: string | null): StoreContext | null {
  if (!storeId) return null;
  return { storeId };
}

export function OfferCampaignNewRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const storeContext = useMemo(() => toStoreContext(storeId), [storeId]);
  const createCampaign = useCreateOfferCampaign(supabase);
  const [submitError, setSubmitError] = useState<OfferCampaignErrorKey | null>(null);

  async function handleSubmit(values: OfferCampaignFormSubmit): Promise<void> {
    setSubmitError(null);
    if (!storeContext) {
      setSubmitError('admin.offerCampaigns.errors.permissionDenied');
      return;
    }

    try {
      await createCampaign.mutateAsync({
        store_id: storeContext.storeId,
        title: values.title,
        short_description: values.short_description,
        starts_at: values.starts_at,
        ends_at: values.ends_at,
        is_active: values.is_active,
        variant_ids: values.variant_ids,
      });
      toast.success(t('admin.offerCampaigns.toasts.created' as TranslationKey));
      navigate('/promotions/campaigns', { replace: true });
    } catch (err) {
      const mapped = mapOfferCampaignError(err);
      setSubmitError(mapped);
      toast.error(t(mapped as TranslationKey));
    }
  }

  return (
    <main dir={dir} data-testid="offer-campaign-new-route" className="bg-background min-h-screen">
      <OfferCampaignFormPage
        mode="create"
        storeId={storeContext?.storeId ?? null}
        isSubmitting={createCampaign.isPending}
        errorKey={submitError}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/promotions/campaigns')}
      />
    </main>
  );
}
